import { Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  DUMMY_USER_ID_PREFIX,
  isDummyUser,
  User,
  UserCoordinates,
  UserData,
  userDataSchema,
  UserStore,
} from '../../state/user.repository';
import { BehaviorSubject, Observable, shareReplay, switchMap } from 'rxjs';
import { AudioMuteStates, UserSettingsStore } from '../../state/user-settings.repository';
import { VideoInput } from '@4players/odin';
import { getAllEntities, updateEntities } from '@ngneat/elf-entities';
import { ParticipantSettingsStore } from '../../state/participant-settings.repository';
import { getRawOption } from '../../shared/helpers/cookie';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  // Canonical stable identity of the own user. Equals the server-issued
  // `andorUUID` (also the `userId` claim baked into every ODIN voice token
  // this client requests). Empty string until `createOwnUser()` runs after
  // `/v1/startup` resolves.
  private _andorUUID = new BehaviorSubject<string>('');
  private _raiseHandTimer: ReturnType<typeof setTimeout> | null = null;

  // Reactive own-user streams. `switchMap` makes them follow the own user's
  // store entry — the entity itself never re-keys, but its `peerId` field
  // and `data` mutate across (re)connects. `shareReplay` collapses
  // consumers onto a single shared upstream.
  readonly ownUser$: Observable<User | undefined> = this._andorUUID.pipe(
    switchMap((id) => this._userStore.get$(id)),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  readonly ownUserData$: Observable<UserData | undefined> = this._andorUUID.pipe(
    switchMap((id) => this._userStore.getUserData$(id)),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  readonly ownCam$: Observable<VideoInput | undefined> = this._andorUUID.pipe(
    switchMap((id) => this._userStore.getCamFromUser$(id)),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  readonly ownScreenCast$: Observable<VideoInput | undefined> = this._andorUUID.pipe(
    switchMap((id) => this._userStore.getScreenCastFromUser$(id)),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  readonly ownUser = toSignal(this.ownUser$, { initialValue: undefined });

  constructor(
    private _userSettingsStore: UserSettingsStore,
    private _userStore: UserStore,
    private _participantSettingsStore: ParticipantSettingsStore
  ) {
    this.profileStateHandler();
  }

  /** The stable string id of the own user (server-issued andorUUID). */
  get ownUserId(): string {
    return this._andorUUID.value;
  }

  /** Current ODIN peer id of the own user, or `null` when not connected. */
  get peerId(): number | null {
    const id = this._andorUUID.value;
    if (!id) return null;
    return this._userStore.get(id)?.peerId ?? null;
  }

  /** Clears the own user's ODIN peer id without re-keying the entity. */
  resetPeerId() {
    const id = this._andorUUID.value;
    if (!id) return;
    this._userStore.setPeerId(id, null);
  }

  /** Sets the own user's current ODIN peer id (called from _onRoomJoined). */
  setPeerId(peerId: number) {
    const id = this._andorUUID.value;
    if (!id) return;
    this._userStore.setPeerId(id, peerId);
  }

  profileStateHandler() {
    this._userSettingsStore.avatar$.subscribe(() => {
      this.updateOwnUserDataFromSettings();
    });
    this._userSettingsStore.userName$.subscribe(() => {
      this.updateOwnUserDataFromSettings();
    });
    this._userSettingsStore.mutedState$.subscribe(() => {
      this.updateOwnUserDataFromSettings();
    });
  }

  toggleStatus() {
    const user = this.ownUser();
    if (!user) return;
    const newStatus = user.data.status === 'online' ? 'away' : 'online';

    if (newStatus === 'away' && this._userSettingsStore.value.mutedState === AudioMuteStates.none) {
      this._userSettingsStore.update({ mutedState: AudioMuteStates.mic });
    }

    this.updateUserData({ status: newStatus });
  }

  updateUserData(data: Partial<UserData>) {
    const user = this.ownUser();
    if (!user) return;
    const updatedUser = {
      ...user,
      data: {
        ...user.data,
        ...data,
      },
    };
    this._userStore.update(updatedUser);
  }

  createUser(
    id: string,
    peerId: number | null,
    data: unknown,
    isSelf: boolean = false,
    coordinates: UserCoordinates = { x: 0, y: 0, z: 0 }
  ): User {
    const oldUser = this._userStore.get(id);
    if (oldUser) {
      // Update peerId and data in place — the entity is stable across
      // reconnects, only the peerId and presence-state change.
      const merged: User = {
        ...oldUser,
        peerId,
        data: { ...oldUser.data, ...userDataSchema.parse(data) },
      };
      this._userStore.update(merged);
      return merged;
    }
    const newUser: Partial<User> = {
      id,
      peerId,
      isSelf,
      coordinates,
      isActiveSpeaker: false,
      isFavorite: false,
    };
    newUser.data = userDataSchema.parse(data);
    const participantSettings = this._participantSettingsStore.get(id);
    if (participantSettings) {
      newUser.isFavorite = participantSettings.isFavorite;
    }

    this._userStore.add(newUser as User);
    return newUser as User;
  }

  createDummyUsers(number: number, breakoutRoomIds: (string | undefined)[]): User[] {
    const createdUsers: User[] = [];
    const allUsers = this._userStore.store.query(getAllEntities());
    const existingDummies = allUsers.filter(isDummyUser);
    const maxExistingIdx = existingDummies.length
      ? Math.max(
          ...existingDummies.map((u) => parseInt(u.id.slice(DUMMY_USER_ID_PREFIX.length), 10) || 0)
        )
      : 0;

    // Generate dummy names from Lord of the Rings and Harry Potter
    const dummyNames = [
      // Lord of the Rings
      'Frodo',
      'Samwise',
      'Gandalf',
      'Aragorn',
      'Legolas',
      'Gimli',
      'Boromir',
      'Merry',
      'Pippin',
      'Arwen',
      'Galadriel',
      'Elrond',
      'Sauron',
      'Gollum',
      'Saruman',
      'Eowyn',
      'Theoden',
      'Faramir',
      'Bilbo',
      'Thorin',
      'Bard',
      'Tauriel',
      'Radagast',
      // Harry Potter
      'Harry',
      'Hermione',
      'Ron',
      'Dumbledore',
      'Snape',
      'McGonagall',
      'Hagrid',
      'Sirius',
      'Lupin',
      'Neville',
      'Luna',
      'Ginny',
      'Draco',
      'Voldemort',
      'Bellatrix',
      'Malfoy',
      'Weasley',
      'Dobby',
      'Hedwig',
      'Buckbeak',
      'Moody',
      'Tonks',
    ];

    for (let i = 0; i < number; i++) {
      const idx = maxExistingIdx + 1 + i;
      const dummyId = `${DUMMY_USER_ID_PREFIX}${idx}`;
      const randomName = dummyNames[Math.floor(Math.random() * dummyNames.length)] + ` ${i + 1}`;

      // Randomly assign a breakout room ID (can be string or undefined)
      const randomBreakoutRoomId =
        breakoutRoomIds.length > 0
          ? breakoutRoomIds[Math.floor(Math.random() * breakoutRoomIds.length)]
          : undefined;

      const userData: UserData = {
        name: randomName,
        status: 'online',
        // Empty -> rendered as a locally generated default from `name`.
        avatar: '',
        inputMuted: false,
        outputMuted: false,
        presentationStartedAt: 0,
        breakoutRoomId: randomBreakoutRoomId,
        joinedAt: null,
      };

      const user = this.createUser(dummyId, null, userData, false);
      createdUsers.push(user);
    }

    return createdUsers;
  }

  /**
   * Creates the own-user entity in the store, keyed by the server-issued
   * `andorUUID`. Must be called after `/v1/startup` has resolved so the id
   * is known via `getRawOption('andorUUID')`. Idempotent — if an entity
   * already exists under that id (returning user, persisted state), the
   * existing one is refreshed from settings.
   */
  createOwnUser() {
    const andorUUID = getRawOption('andorUUID');
    if (!andorUUID) {
      console.warn('createOwnUser called before andorUUID was known');
      return undefined;
    }
    this._andorUUID.next(andorUUID);
    const userName = this._userSettingsStore.value.userName;

    if (!this._userStore.has(andorUUID)) {
      const userSettings = this._userSettingsStore.value;
      const mutedState = userSettings.mutedState;
      const userData: UserData = {
        status: 'online',
        avatar: userSettings.avatar,
        name: userName || 'Guest',
        inputMuted: mutedState !== AudioMuteStates.none,
        outputMuted: mutedState !== AudioMuteStates.none && mutedState !== AudioMuteStates.mic,
        presentationStartedAt: 0,
        joinedAt: null,
      };
      return this.createUser(andorUUID, null, userData, true);
    }
    this.updateOwnUserDataFromSettings();
    return this.ownUser();
  }

  updateOwnUser(user: Partial<User>) {
    const id = this._andorUUID.value;
    if (!id) return;
    this._userStore.store.update(
      updateEntities(id, (state) => ({
        ...state,
        ...user,
      }))
    );
  }

  updateOwnUserDataFromSettings() {
    const userName = this._userSettingsStore.value.userName;
    const avatar = this._userSettingsStore.value.avatar;
    const mutedState = this._userSettingsStore.value.mutedState;
    const id = this._andorUUID.value;
    if (!id) return;
    const user = this._userStore.get(id);
    const userData = user?.data;
    if (userData) {
      const userDataUpdated = { ...userData };
      userDataUpdated.name = userName;
      userDataUpdated.avatar = avatar;

      switch (mutedState) {
        case AudioMuteStates.none: {
          userDataUpdated.inputMuted = false;
          userDataUpdated.outputMuted = false;
          userDataUpdated.status = 'online';
          break;
        }
        case AudioMuteStates.mic: {
          userDataUpdated.inputMuted = true;
          userDataUpdated.outputMuted = false;
          break;
        }
        case AudioMuteStates.speakers: {
          userDataUpdated.inputMuted = true;
          userDataUpdated.outputMuted = true;
          break;
        }
        case AudioMuteStates.speakersKeepMic: {
          userDataUpdated.inputMuted = true;
          userDataUpdated.outputMuted = true;
          break;
        }
      }
      this._userStore.update({ ...user, data: userDataUpdated });
    }
  }

  updateRemoteUserData(userId: string, data: unknown) {
    const user = this._userStore.get(userId);
    if (user) {
      const parsedData = userDataSchema.safeParse(data);
      if (parsedData.success) {
        const updatedUser = {
          ...user,
          data: {
            ...user.data,
            ...parsedData.data,
          },
        };
        this._userStore.update(updatedUser);
      }
    }
  }

  addCam(media: VideoInput) {
    const user = this.ownUser();
    if (!user) return;
    const updatedUser = { ...user };
    updatedUser.camStream = media;
    this._userStore.update(updatedUser);
  }

  updateUserVolume(volume: number, user: User) {
    const refreshedUser = this._userStore.get(user.id);
    if (refreshedUser) {
      refreshedUser.coordinates = { x: 0, y: 0, z: 0 };
      refreshedUser.audioOutput?.setVolume(volume);
    }
    const participantSettings = this._participantSettingsStore.get(user.id);
    if (participantSettings) {
      const updatedParticipantSettings = {
        ...participantSettings,
        volume,
      };
      this._participantSettingsStore.update(updatedParticipantSettings);
    } else {
      this._participantSettingsStore.add({
        id: user.id,
        volume,
        isFavorite: user.isFavorite,
      });
    }
  }

  updateFavorite(userId: string): void {
    const user = this._userStore.get(userId);
    if (!user) return;
    const updatedUser = { ...user, isFavorite: !user.isFavorite };
    const participantSettings = this._participantSettingsStore.get(user.id);
    if (participantSettings) {
      const updatedParticipantSettings = {
        ...participantSettings,
        isFavorite: updatedUser.isFavorite,
      };
      this._participantSettingsStore.update(updatedParticipantSettings);
    } else {
      this._participantSettingsStore.add({
        id: user.id,
        volume: 1,
        isFavorite: updatedUser.isFavorite,
      });
    }
    this._userStore.update(updatedUser);
  }

  /**
   * Stores the own published screen share, or clears it. Every share is a
   * presentation: `presentationStartedAt` carries its start time, which
   * lets every client agree that the latest share presents. The stamp is
   * above every known one, so a new share takes over even when this
   * clock runs behind a previous presenter's. One update on the current
   * entity: a copy taken before the publish's awaits would undo user data
   * changed meanwhile.
   */
  setOwnScreenCast(video: VideoInput | undefined) {
    const id = this._andorUUID.value;
    const own = id ? this._userStore.get(id) : undefined;
    if (!own) return;
    // Clearing an already cleared share would publish unchanged user data.
    if (!video && !own.screenCastStream && own.data.presentationStartedAt === 0) return;
    let startedAt = 0;
    if (video) {
      const latest = Math.max(
        0,
        ...this._userStore.store.query(getAllEntities()).map((u) => u.data.presentationStartedAt)
      );
      startedAt = Math.max(Date.now(), latest + 1);
    }
    this._userStore.store.update(
      updateEntities(id, (user) => ({
        ...user,
        screenCastStream: video,
        data: { ...user.data, presentationStartedAt: startedAt },
      }))
    );
  }

  toggleRaiseHand() {
    const user = this.ownUser();
    if (!user) return;
    const newValue = !user.data.hasRaisedHand;
    this.updateUserData({ hasRaisedHand: newValue });

    if (this._raiseHandTimer) clearTimeout(this._raiseHandTimer);
    if (newValue) {
      this._raiseHandTimer = setTimeout(
        () => {
          this.updateUserData({ hasRaisedHand: false });
        },
        5 * 60 * 1000
      );
    }
  }

  clearRaiseHandTimer() {
    if (this._raiseHandTimer) {
      clearTimeout(this._raiseHandTimer);
      this._raiseHandTimer = null;
    }
  }
}
